import { FC, useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";

interface EditableTitleProps {
  value: string;
  onSave: (next: string) => void;
  placeholder?: string;
  variant?: "block" | "view";
  disabled?: boolean;
}

export const EditableTitle: FC<EditableTitleProps> = ({
  value,
  onSave,
  placeholder = "Untitled",
  variant = "block",
  disabled = false,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const commit = () => {
    const next = draft.trim();
    setEditing(false);
    if (next && next !== value) onSave(next);
    else setDraft(value);
  };

  if (editing) {
    return (
      <Input
        ref={inputRef}
        $variant={variant}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
          } else if (e.key === "Escape") {
            e.preventDefault();
            setDraft(value);
            setEditing(false);
          }
        }}
        placeholder={placeholder}
      />
    );
  }

  return (
    <Display $variant={variant} $disabled={disabled} onClick={() => !disabled && setEditing(true)}>
      {value || <Placeholder>{placeholder}</Placeholder>}
    </Display>
  );
};

const Display = styled.div<{ $variant: "block" | "view"; $disabled: boolean }>`
  cursor: ${({ $disabled }) => ($disabled ? "default" : "text")};
  font-weight: ${({ $variant }) => ($variant === "view" ? 700 : 600)};
  font-size: ${({ $variant }) => ($variant === "view" ? "42px" : "15px")};
  line-height: ${({ $variant }) => ($variant === "view" ? "1.1" : "1.2")};
  letter-spacing: ${({ $variant }) => ($variant === "view" ? "-0.02em" : "normal")};
  padding: 2px 4px;
  margin: -2px -4px;
  border-radius: 4px;
  &:hover {
    background: ${({ $disabled, theme }) =>
      $disabled ? "transparent" : theme.colors.background.button.onChrome.hover};
  }
`;

const Input = styled.input<{ $variant: "block" | "view" }>`
  font-weight: ${({ $variant }) => ($variant === "view" ? 700 : 600)};
  font-size: ${({ $variant }) => ($variant === "view" ? "42px" : "15px")};
  line-height: ${({ $variant }) => ($variant === "view" ? "1.1" : "1.2")};
  letter-spacing: ${({ $variant }) => ($variant === "view" ? "-0.02em" : "normal")};
  border: none;
  background: ${({ theme }) => theme.colors.background.button.onChrome.hover};
  padding: 2px 4px;
  margin: -2px -4px;
  border-radius: 4px;
  width: 100%;
  outline: none;
  font-family: inherit;
  color: inherit;
`;

const Placeholder = styled.span`
  color: ${({ theme }) => theme.colors.text.tertiary};
`;
